<template>
  <div
    class="fit"
    style="position: relative; min-width: 476px; min-height: 476px;"
  >
    <div
      class="time-clock-modal-container"
    >
      <div
        v-if="currentSite.timeClockConfig.job_type_enabled && hasJobTypes && showJobTypeDialog"
        class="time-clock-modal-content"
      >
        <q-card>
          <q-card-section class="row items-center q-mx-xs">
            <div class="text-h6">
              Select Job Type
            </div>
            <q-space />
            <q-btn
              v-close-popup
              icon="close"
              flat
              round
              dense
              @click="showJobTypeDialog = false"
            />
          </q-card-section>
          <q-card-section>
            <q-select
              v-model="userEmployeeJobType"
              option-label="name"
              :options="currentUser.userEmployeeJobTypesForSite"
              map-options
              :disabled="activeTimeClockSession.isRemote"
              @update:model-value="showJobTypeDialog = false"
            />
          </q-card-section>
        </q-card>
      </div>
      <svg
        viewBox="0 0 450 450"
        class="q-mx-auto"
        style="height: 450px; width: 450px; position: absolute; top: 13; left: 13; transform: rotate(-90deg);"
      >
        <circle
          cx="225"
          cy="225"
          :r="timeClockRadius"
        />
        <circle
          v-for="timeClockSegment, index in timeClockSegments"
          :key="index"
          :class="timeClockSegment.type === 'clocked-on' && index === timeClockSegments.length - 1 ? 'active-clocked-on' : timeClockSegment.type"
          :style="{ 'stroke-width': index === timeClockSegments.length - 1 ? '18px': '' }"
          cx="225"
          cy="225"
          :r="timeClockRadius"
          :stroke-dasharray="timeClockSegment.size + ' ' + timeClockCircumference"
          :stroke-dashoffset="-timeClockSegment.offset"
        />
      </svg>
      <div>
        <div
          class="column justify-center q-mx-auto"
          style="min-height: 436px; max-width: 306px;"
        >
          <div
            class="row justify-between q-mx-md"
            style="height: 66px; margin-bottom: -16px;"
          >
            <div
              v-if="activeTimeClockSession.startedAt"
              class="col-6 column q-gutter-y-xs"
            >
              <span
                class="text-h3"
                style="font-size: 0.9rem; line-height: 0.9rem;"
              >
                Clocked on
              </span>
              <span
                class="text-h3 text-uppercase"
                style="font-size: 1.5rem; line-height: 1.5rem;"
              >
                {{ $d(activeTimeClockSession.startedAt, 'shortTime') }}
              </span>
            </div>
            <div
              v-else
              class="col-6"
            />
            <div
              class="col-6 items-end column q-gutter-y-xs"
            >
              <span
                class="text-h3"
                style="font-size: 0.9rem; line-height: 0.9rem; color: darkslateblue;"
              >
                Total Today
              </span>
              <span
                class="text-h3"
                style="font-size: 1.5rem; line-height: 1.5rem; color: darkslateblue;"
              >
                {{ String(totalHours).padStart(2, '0') }}:{{ String(totalMinutes).padStart(2, '0') }}:{{ String(totalSeconds).padStart(2, '0') }}
              </span>
            </div>
          </div>
          <div
            class="row justify-center"
          >
            <span
              class="text-h3"
              style="font-size: 5.5rem; line-height: 5.5rem; color: darkslateblue;"
            >
              {{ String(hours).padStart(2, '0') }}:{{ String(minutes).padStart(2, '0') }}
            </span>
            <span
              class="text-h3 q-ml-xs q-mb-sm"
              style="font-size: 2rem; line-height: 2rem; margin-top: auto; color: darkslateblue;"
            >
              :{{ String(seconds).padStart(2, '0') }}
            </span>
          </div>
          <q-separator class="q-mx-sm q-mb-lg" />
          <div
            class="row q-gutter-x-md q-mx-auto q-mb-md"
          >
            <q-btn
              v-if="currentSite.timeClockConfig.job_type_enabled && hasJobTypes"
              style="cursor: pointer"
              square
              outline
              :disabled="activeTimeClockSession.isRemote"
              @click="showJobTypeDialog = true"
            >
              Job Type: {{ jobTypeDisplay }}
            </q-btn>
          </div>
          <div class="row q-gutter-x-md q-mx-auto">
            <q-btn
              class="q-ml-none"
              style="width: 145px; height: 40px;"
              icon="logout"
              color="negative"
              outline
              @click="$router.push('/logout')"
            >
              Log Out
            </q-btn>

            <div>
              <q-btn
                v-if="!activeTimeClockSession.startedAt"
                style="width: 145px; height: 40px;"
                icon="play_arrow"
                color="positive"
                :disabled="(currentSite.timeClockConfig.job_type_enabled && hasJobTypes && !userEmployeeJobType)"
                @click="start"
              >
                Clock On
              </q-btn>
              <q-btn
                v-else
                style="width: 145px; height: 40px;"
                icon="stop"
                color="negative"
                @click="stop"
              >
                Clock Off
              </q-btn>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import TimeClockSession from 'src/models/time-clock/TimeClockSession'

const timeClockRadius = 215
const timeClockCircumference = 1350.88
const timeClockMs = 24 * 60 * 60 * 1000

export default {
  name: 'TimeClock',
  context: ['currentUser'],
  configuration: ['currentSite'],
  models: {
    activeTimeClockSession: {
      employeeJobType: 'default',
      variables () {
        return {
          userId: this.currentUser?.id,
          skip: !this.currentUser?.id || !this.currentSite?.timeClockConfig?.time_clock_enabled
        }
      },
      immediate: true
    },
    recentTimeClockSessions: {
      variables () {
        return {
          userId: this.currentUser?.id,
          skip: !this.currentUser?.id || !this.currentSite?.timeClockConfig?.time_clock_enabled
        }
      },
      immediate: true
    }
  },
  emits: ['clock-on', 'clock-off'],
  data () {
    return {
      hours: 0,
      minutes: 0,
      seconds: 0,
      totalHours: 0,
      totalMinutes: 0,
      totalSeconds: 0,
      userEmployeeJobType: null,
      showJobTypeDialog: true
    }
  },
  computed: {
    timeClockSegments () {
      const segments = []

      _.each(this.recentTimeClockSessions.data, (recentTimeClockSession, index) => {
        if (index > 0) {
          const type = 'clocked-off'

          // Fill in the elapsed time between sessions
          const previousSessionEndTime = new Date(this.recentTimeClockSessions.data[index - 1].endedAt).getTime()
          const clockedOffDuration = new Date(recentTimeClockSession.startedAt).getTime() - previousSessionEndTime

          const size = timeClockCircumference / timeClockMs * clockedOffDuration
          let offset = 0
          for (let i = segments.length - 1; i >= 0; i--) {
            offset += segments[i].size
          }

          segments.push({
            size,
            offset,
            type
          })
        }

        const type = 'clocked-on'
        const size = timeClockCircumference / timeClockMs * recentTimeClockSession.duration
        let offset = 0
        for (let i = segments.length - 1; i >= 0; i--) {
          offset += segments[i].size
        }

        segments.push({
          size,
          offset,
          type
        })
      })

      if (this.recentTimeClockSessions?.data?.length) {
        const type = 'clocked-off'

        // Fill in the elapsed time between sessions
        const previousSessionEndTime = new Date(this.recentTimeClockSessions.data[this.recentTimeClockSessions.data.length - 1].endedAt).getTime()
        const clockedOffDuration = new Date(this.activeTimeClockSession.startedAt || Date.now()).getTime() - previousSessionEndTime

        const size = timeClockCircumference / timeClockMs * clockedOffDuration
        let offset = 0
        for (let i = segments.length - 1; i >= 0; i--) {
          offset += segments[i].size
        }

        segments.push({
          size,
          offset,
          type
        })
      }

      if (this.activeTimeClockSession.startedAt) {
        const type = 'clocked-on'
        const size = timeClockCircumference / timeClockMs * this.activeTimeClockSession.duration
        let offset = 0
        for (let i = segments.length - 1; i >= 0; i--) {
          offset += segments[i].size
        }

        segments.push({
          size,
          offset,
          type
        })
      }

      return segments
    },
    jobTypeDisplay () {
      return this.userEmployeeJobType ? this.userEmployeeJobType.name : 'None'
    },
    hasJobTypes () {
      return this.currentUser.userEmployeeJobTypesForSite?.data?.length > 0
    }
  },
  watch: {
    'recentTimeClockSessions.data.length': function () {
      const totalDuration = _.sumBy(this.recentTimeClockSessions.data, 'duration') + this.activeTimeClockSession.duration
      this.totalHours = Math.floor(totalDuration / (1000 * 60 * 60))
      this.totalMinutes = Math.floor((totalDuration / (1000 * 60)) % 60)
      this.totalSeconds = Math.floor((totalDuration / 1000) % 60)
    },
    'activeTimeClockSession.id': function () {
      this.hours = this.activeTimeClockSession.hours
      this.minutes = this.activeTimeClockSession.minutes
      this.seconds = this.activeTimeClockSession.seconds

      const totalDuration = _.sumBy(this.recentTimeClockSessions.data, 'duration') + this.activeTimeClockSession.duration
      this.totalHours = Math.floor(totalDuration / (1000 * 60 * 60))
      this.totalMinutes = Math.floor((totalDuration / (1000 * 60)) % 60)
      this.totalSeconds = Math.floor((totalDuration / 1000) % 60)

      if (this.currentSite.timeClockConfig.job_type_enabled && this.activeTimeClockSession.employeeJobType) {
        this.selectJobType('session')
      } else {
        this.selectJobType('default')
      }
    },
    'currentUser.userEmployeeJobTypes.data.length': function () {
      if (this.currentSite.timeClockConfig.job_type_enabled) {
        this.selectJobType('default')
      }
    }
  },
  created () {
    _.extend(this, { timeClockRadius, timeClockCircumference, timeClockMs })
  },
  mounted () {
    if (this.currentSite.timeClockConfig?.time_clock_enabled) {
      this.clockTickerIntervalId = setInterval(() => {
        if (this.activeTimeClockSession?.startedAt) {
          this.seconds = this.seconds === 59 ? 0 : this.seconds + 1
          if (this.seconds === 0) {
            this.minutes = this.minutes === 59 ? 0 : this.minutes + 1
          }
          if (this.minutes === 0 && this.seconds === 0) {
            this.hours += 1
          }

          this.totalSeconds = this.totalSeconds === 59 ? 0 : this.totalSeconds + 1
          if (this.totalSeconds === 0) {
            this.totalMinutes = this.totalMinutes === 59 ? 0 : this.totalMinutes + 1
          }
          if (this.totalMinutes === 0 && this.totalSeconds === 0) {
            this.totalHours += 1
          }
        }
      }, 1000)

      this.clockSessionsIntervalId = setInterval(() => {
        if (this.currentUser?.id && this.currentSite?.timeClockConfig?.time_clock_enabled) {
          this.activeTimeClockSession.fetch(true)
          this.recentTimeClockSessions.fetch(true)
        }
      }, 5 * 60 * 1000)
    }
  },
  beforeUnmount () {
    if (this.clockTickerIntervalId) {
      clearInterval(this.clockTickerIntervalId)
    }

    if (this.clockSessionsIntervalId) {
      clearInterval(this.clockSessionsIntervalId)
    }
  },
  methods: {
    async start () {
      const newTimeClockSession = new TimeClockSession({ user: this.currentUser, employeeJobType: this.userEmployeeJobType?.employeeJobType, hourlyRate: this.userEmployeeJobType?.hourlyRate })
      await newTimeClockSession.start()
      newTimeClockSession.destroy()
      await this.activeTimeClockSession.fetch(true)
      this.$emit('clock-on', { activeTimeClockSession: this.activeTimeClockSession })
    },
    async stop () {
      await this.activeTimeClockSession.stop()
      await this.activeTimeClockSession.fetch(true)
      await this.recentTimeClockSessions.fetch(true)

      this.hours = 0
      this.minutes = 0
      this.seconds = 0

      this.$emit('clock-off', { activeTimeClockSession: this.activeTimeClockSession, recentTimeClockSessions: this.recentTimeClockSessions })
    },
    selectJobType (config = 'default') {
      if (config === 'default' && (!this.userEmployeeJobType && this.hasJobTypes)) {
        this.userEmployeeJobType = this.currentUser.defaultEmployeeJobType
      } else if (config === 'session' && this.activeTimeClockSession.employeeJobType) {
        this.showJobTypeDialog = false
        this.userEmployeeJobType = this.currentUser.userEmployeeJobTypes.data.find(userEmployeeJobType => userEmployeeJobType.employeeJobType.id === this.activeTimeClockSession.employeeJobType.id)
      }
    }
  }
}

</script>

<style lang="scss" scoped>
  circle {
    stroke: $grey-4;
    stroke-width: 14px;
    fill: transparent;
  }
  .active-clocked-on {
    stroke: $lime-14;
  }
  .clocked-on {
    stroke: $green-7;
  }
  .clocked-off {
    stroke: $blue-grey-3
  }

  .time-clock-modal-container {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    min-width: 476px;
    min-height: 476px;
  }

  .time-clock-modal-content {
    position: absolute;
    background-color: rgb(253, 253, 253);
    border: 1px solid #ccc;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    z-index: 1000;
    transform: translate(-50%, -50%);
    top: 50%;
    left: 50%;
    border-radius: 5px;
    width: 300px;
  }

</style>
